import { SectionsTreeNode } from '../types';
import { ChevronIcon, KebabMenuIcon } from '../../../atoms';
import { classNames } from 'primereact/utils';
import { MouseEvent, useMemo } from 'react';
import { NodeApi, NodeRendererProps } from 'react-arborist';
import { PersistentInputState } from '../hooks';
import { SectionsTreeNodeContent } from './SectionsTreeNodeContent';
import { SectionRenameInput } from './SectionRenameInput';

const FolderArrow = ({ node, rowHighlight }: { node: NodeApi<SectionsTreeNode>; rowHighlight?: 'green' | 'grey' }) => {
  return (
    <>
      {node.isInternal ? (
        <div
          className={classNames(
            'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center',
            'rounded-[4px]',
            rowHighlight === 'green' ? 'hover:bg-fletchers-green-100' : 'hover:bg-fletchers-gray-95',
          )}
          onClick={(event: MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (node.isOpen) {
              node.close();
            } else {
              node.open();
            }
          }}
        >
          {node.isOpen ? (
            <ChevronIcon className="translate-y-px rotate-180 [&>path]:stroke-black" width={11} height={7} />
          ) : (
            <ChevronIcon className="translate-y-px rotate-0 [&>path]:stroke-black" width={11} height={7} />
          )}
        </div>
      ) : null}
    </>
  );
};

export interface SuggestionItem {
  id: string;
  name: string;
}

export interface SuggestionsMapProps {
  [type: string]: SuggestionItem[];
}
interface BundleSectionsTreeNodeTemplateInputInputProps {
  node: NodeApi<SectionsTreeNode>;
  suggestionsMap?: SuggestionsMapProps;
  persistentInputState: PersistentInputState<string | SuggestionItem, SuggestionItem>;
}

export const BundleSectionsTreeNodeTemplateInput: React.FC<BundleSectionsTreeNodeTemplateInputInputProps> = ({
  node,
  suggestionsMap,
  persistentInputState,
}) => {
  const {
    data: { value },
    setValue,
    reset,
  } = persistentInputState;

  const initialText = typeof value === 'string' ? value : value?.name || '';

  const suggestions = useMemo(() => {
    const suggestionsType = node.data.type;
    if (!suggestionsMap || !(suggestionsType in suggestionsMap)) return [];
    const list = suggestionsMap[suggestionsType];
    return Array.isArray(list) ? list : [];
  }, [suggestionsMap, node.data.type]);

  return (
    <SectionRenameInput
      initialValue={initialText}
      suggestions={suggestions}
      onChange={(next) => setValue(next)}
      onCommit={(finalValue) => {
        node.submit(JSON.stringify(finalValue));
        reset();
      }}
      onCancel={() => {
        reset();
        node.reset();
      }}
    />
  );
};

interface BundleSectionsTreeNodeTemplateProps extends NodeRendererProps<SectionsTreeNode> {
  suggestionsMap?: SuggestionsMapProps;
  persistentInputState?: PersistentInputState<string | SuggestionItem, SuggestionItem>;
  rowHighlight?: 'green' | 'grey';
  onEllipsisClick?: (e: MouseEvent<HTMLButtonElement>, node: NodeApi<SectionsTreeNode>) => void;
  isRowHovered?: boolean;
}

export const BundleSectionsTreeNodeTemplate = ({
  node,
  style,
  dragHandle,
  suggestionsMap,
  persistentInputState,
  rowHighlight,
  onEllipsisClick,
  isRowHovered,
}: BundleSectionsTreeNodeTemplateProps) => {
  return (
    <div
      style={{ ...style }}
      ref={dragHandle}
      className={classNames('flex w-full flex-row items-center justify-between gap-1')}
    >
      <>
        {node.isEditing ? (
          <div className={classNames('flex min-w-0 flex-1 flex-col justify-center')}>
            <BundleSectionsTreeNodeTemplateInput
              node={node}
              suggestionsMap={suggestionsMap}
              persistentInputState={
                // Type added because I changed Tree component props
                persistentInputState as PersistentInputState<string | SuggestionItem, SuggestionItem>
              }
            />
          </div>
        ) : (
          <SectionsTreeNodeContent data={node.data} />
        )}
        {isRowHovered && onEllipsisClick && !node.isEditing && (
          <button
            type="button"
            aria-label="Section options"
            onClick={(event) => {
              event.stopPropagation();
              onEllipsisClick(event, node);
            }}
            className={classNames(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center',
              'rounded-[4px]',
              'focus:outline-none',
              rowHighlight === 'green' ? 'hover:bg-fletchers-green-100' : 'hover:bg-fletchers-gray-95',
            )}
          >
            <KebabMenuIcon className="text-black" />
          </button>
        )}
        <FolderArrow node={node} rowHighlight={rowHighlight} />
      </>
    </div>
  );
};
